import { useState, type ReactNode } from "react";
import { ArrowLeftRight, Lock, type LucideIcon } from "lucide-react";
import { PatientCombobox } from "@/components/agenda/PatientCombobox";

/**
 * Cartão do topo do formulário do pedido (mesmo desenho do `LockedInfoBox`) que pode ser trocado
 * ali mesmo: "Trocar" vira o seletor no lugar do valor. Sem `onTrocar`, fica travado com cadeado.
 */
export function CartaoTrocavel({
  icon: Icon,
  label,
  value,
  trocando,
  onTrocar,
  onCancelar,
  children,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  trocando: boolean;
  onTrocar?: () => void;
  onCancelar: () => void;
  /** O seletor, mostrado enquanto troca. */
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2 dark:border-border dark:bg-muted/30">
      <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-100 text-brand-700 sm:flex dark:bg-primary/15 dark:text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:text-xs dark:text-muted-foreground">{label}</p>
          {trocando ? (
            <button type="button" onClick={onCancelar} className="text-[12px] font-semibold text-zinc-600 hover:underline dark:text-muted-foreground">
              Cancelar
            </button>
          ) : onTrocar ? (
            <button type="button" onClick={onTrocar} className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700 hover:underline dark:text-primary">
              <ArrowLeftRight className="h-3 w-3" />
              Trocar
            </button>
          ) : (
            <Lock className="h-3.5 w-3.5 shrink-0 text-zinc-300 dark:text-muted-foreground/60" />
          )}
        </div>
        {trocando ? (
          <div className="mt-1">{children}</div>
        ) : (
          <p className="truncate text-[13px] font-semibold text-zinc-900 sm:text-base dark:text-foreground">{value}</p>
        )}
      </div>
    </div>
  );
}

type Paciente = { id: string | null; name: string; phone: string };

/** Busca de paciente da clínica pra trocar no pedido. Só vale paciente cadastrado: o combobox
 *  avisa a cada letra digitada, e a troca acontece quando um da lista é escolhido. */
export function TrocarPaciente({ onEscolher }: { onEscolher: (p: Paciente) => void }) {
  const [rascunho, setRascunho] = useState<Paciente>({ id: null, name: "", phone: "" });
  return (
    <PatientCombobox
      value={rascunho}
      autoFocus
      allowWalkIn={false}
      placeholder="Buscar paciente..."
      onChange={(p) => (p.id ? onEscolher(p) : setRascunho(p))}
    />
  );
}
